import React, { useState } from "react";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { toast } from "react-toastify";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { payPayrollRun } from "features/Payroll/services/PayrollService";
import type { PayrollRun } from "features/Payroll/types/payroll.types";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type PayRunConfirmModalProps = {
  run: PayrollRun | null;
  /** Whether any item in the run has a positive net amount — if not, no mode of payment is needed. */
  hasPositiveNetItem: boolean;
  isOpen: boolean;
  onClose: () => void;
  onPaid?: () => void | Promise<void>;
};

const PayRunConfirmModal = ({
  run,
  hasPositiveNetItem,
  isOpen,
  onClose,
  onPaid,
}: PayRunConfirmModalProps) => {
  const [modeOfPaymentId, setModeOfPaymentId] = useState<number | "">("");
  const [paymentDate, setPaymentDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleClose = () => {
    if (busy) return;
    setModeOfPaymentId("");
    setPaymentDate("");
    setReferenceNumber("");
    setAttempted(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!run) return;
    setAttempted(true);
    if (hasPositiveNetItem && !modeOfPaymentId) return;
    setBusy(true);
    try {
      const res = await payPayrollRun(run.id, {
        modeOfPaymentId: modeOfPaymentId ? Number(modeOfPaymentId) : undefined,
        paymentDate: paymentDate || null,
        referenceNumber: referenceNumber.trim() || null,
      });
      if (res.success) {
        toast.success(res.message ?? "Payroll run paid.");
        handleClose();
        await onPaid?.();
      } else {
        toast.error(res.message ?? "Could not pay this run.");
      }
    } catch (err) {
      toast.error(axiosMessage(err, "Could not pay this run."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal fade isOpen={isOpen && run != null} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        Pay run {run?.code ?? `#${run?.id}`}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          This pays every employee in this run in a single transaction. If
          anything fails, nothing is paid and the run stays{" "}
          <strong>Finalized</strong>.
        </p>
        {hasPositiveNetItem ? (
          <>
            <div className="mb-3">
              <Label className="form-label">
                Mode of payment <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getModeOfPaymentOptions}
                placeholder="Search modes…"
                value={modeOfPaymentId}
                onChange={(val: any) => setModeOfPaymentId(val?.value ?? "")}
                isInvalid={attempted && !modeOfPaymentId}
              />
              {attempted && !modeOfPaymentId ? (
                <FormFeedback type="invalid" className="d-block">
                  Mode of payment is required.
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label className="form-label">
                Payment date <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Flatpickr
                className="form-control"
                placeholder="Select date"
                options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
                onChange={(date: any) =>
                  setPaymentDate(date[0] ? moment(date[0]).format("YYYY-MM-DD") : "")
                }
                value={paymentDate ? new Date(paymentDate) : ""}
                disabled={busy}
              />
            </div>
            <div className="mb-0">
              <Label className="form-label">
                Reference number{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Input
                type="text"
                placeholder="UTR, cheque no., bank batch id…"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                disabled={busy}
              />
            </div>
          </>
        ) : (
          <p className="text-muted mb-0">
            Every item in this run nets to ₹0 — no payment method is needed,
            items will just move straight to <strong>Paid</strong>.
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={handleClose}>
          Cancel
        </Button>
        <Button color="success" disabled={busy} onClick={() => void handleConfirm()}>
          {busy ? "Paying…" : "Pay run"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PayRunConfirmModal;
